import type { EntryView, ReplyView } from "../api/types.js";

/**
 * 一条日记在界面上的归属与能力判断。
 *
 * ⚠️ 这里的判断**只决定「显示什么」**，不决定「能拿到什么」—— 拿不到的
 * 日记根本不会出现在列表里。可见性由后端在查询层强制（spec §6.6），
 * 前端拿到的每一条本来就有权看到。把这些判断集中在这里，是为了让「我写的
 * 私密日记显示锁形徽标」这类呈现规则只有一个来源。
 */

export function isMine(entry: EntryView, meId: string): boolean {
  return entry.ownerId === meId;
}

/** 只有作者本人能编辑 / 迁移可见性。 */
export function canEdit(entry: EntryView, meId: string): boolean {
  return isMine(entry, meId);
}

/**
 * 「TA 已读」是否该出现。
 *
 * 三条同时成立才显示：作者是我、日记是共享的、`readAt` 有值。
 * **绝不显示「未读」** —— spec §5.4.4 明说未读时该区域不出现任何标记，
 * 显示未读等于给「TA 还没看」装一个倒计时。
 */
export function showsReadReceipt(entry: EntryView, meId: string): boolean {
  return isMine(entry, meId) && entry.visibility === "shared" && entry.readAt !== null;
}

/**
 * 我是否可以在这篇日记上写回应。
 *
 * 必须：不是我写的、共享的、允许回应。私密日记不产生任何回应入口
 * （spec §5.4.3），作者自己的日记也没有 —— 回应是「回 TA 一句」。
 */
export function canReply(entry: EntryView, meId: string): boolean {
  return !isMine(entry, meId) && entry.visibility === "shared" && entry.allowReply;
}

/**
 * 我是否能读到回应列表。
 *
 * 只有共享日记有回应通道，对**双方**都成立：作者看自己的私密日记也拿不到
 * 回应（spec §5.4.2 要求私密详情页写「这条通道对任何人都无效，包括 TA」）。
 * 作者看自己的共享日记可以读到列表（含 TA 的回应与自己的）。
 */
export function canReadReplies(entry: EntryView): boolean {
  return entry.visibility === "shared";
}

/** 我是否能删这条回应 —— 只有作者本人。 */
export function canDeleteReply(reply: ReplyView, meId: string): boolean {
  return reply.authorId === meId;
}

/** 可见性徽标文案。 */
export function visibilityLabel(entry: EntryView): string {
  return entry.visibility === "shared" ? "已共享" : "仅自己可见";
}
